import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, Share2 } from 'lucide-react';
import { getBlogBySlug, BlogPost as ApiBlogPost } from '../services/blog';
import Loader from '../components/Loader';

interface BlogPost {
  id: string;
  title: string;
  excerpt: string;
  content: string;
  author: string;
  publishedDate: string;
  readTime: string;
  category: string;
  image: string;
  slug: string;
}

interface BlogPostPageProps {
  initialData?: {
    blogPost?: BlogPost;
  };
}

const BlogPostPage = ({ initialData }: BlogPostPageProps) => {
  const { slug } = useParams<{ slug: string }>();
  const [post, setPost] = useState<BlogPost | null>(initialData?.blogPost || null);
  const [loading, setLoading] = useState(!initialData?.blogPost); // Don't show loading if we have initial data
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Only fetch if we don't have initial data
    if (!initialData?.blogPost) {
      const fetchPost = async () => {
        if (!slug) return;
        
        setLoading(true);
        setError(null);
        
        try {
          // Fetch the specific blog post by slug
          const response = await getBlogBySlug(slug);
          
          if (response.success && response.data) {
            const foundPost = response.data as ApiBlogPost;
            
            // Convert API blog post to local interface
            const localPost: BlogPost = {
              id: foundPost._id || '',
              title: foundPost.title,
              excerpt: foundPost.excerpt,
              content: foundPost.content,
              author: foundPost.author,
              publishedDate: foundPost.createdAt || new Date().toISOString(),
              readTime: '5 min read', // Default read time
              category: typeof foundPost.category === 'object' ? foundPost.category.name : foundPost.category,
              image: foundPost.image || '',
              slug: foundPost.slug
            };
            console.log('Blog post data:', localPost);
            console.log('Blog post image:', foundPost.image);
            console.log('Original category from API:', foundPost.category);
            setPost(localPost);
          } else {
            setError('Failed to fetch blog post');
            setPost(null);
          }
        } catch (err) {
          console.error('Error fetching blog post:', err);
          setError('Failed to fetch blog post');
          setPost(null);
        } finally {
          setLoading(false);
        }
      };

      fetchPost();
    }
  }, [slug, initialData?.blogPost]);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const handleShare = () => {
    if (!import.meta.env.SSR && typeof window !== 'undefined') {
      if (navigator.share) {
        navigator.share({
          title: post?.title,
          text: post?.excerpt,
          url: window.location.href,
        });
      } else {
        // Fallback: copy to clipboard
        navigator.clipboard.writeText(window.location.href);
        alert('Link copied to clipboard!');
      }
    }
  };

  if (loading) {
    return (
      <Loader variant="fullscreen" text="Loading blog post..." />
    );
  }

  if (!post) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">Post Not Found</h1>
          <p className="text-gray-600 dark:text-gray-300 mb-6">
            {error || "The blog post you're looking for doesn't exist."}
          </p>
          <Link
            to="/blog"
            className="inline-flex items-center px-4 py-2 bg-green-800 hover:bg-green-900 text-white rounded-[24px] transition-colors duration-200"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Blog
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <section className="py-12">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Title */}
          <h1 className="text-4xl md:text-5xl font-extrabold text-center text-gray-900 dark:text-white mb-4">
            {post.title}
          </h1>
          {/* Excerpt */}
          {post.excerpt && (
            <p className="text-xl text-center text-gray-500 dark:text-gray-300 mb-6">
              {post.excerpt}
            </p>
          )}
          {/* Author/Date/Category */}
          <div className="flex flex-wrap items-center justify-center text-sm text-gray-400 dark:text-gray-500 mb-6 gap-4">
            <span>{post.author}</span>
            <span>•</span>
            <span>{formatDate(post.publishedDate)}</span>
            {post.category && (
              <>
                <span>•</span>
                <span className="bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300 px-3 py-1 rounded-full text-xs font-medium">{post.category}</span>
              </>
            )}
          </div>
          {/* Share Button */}
          <div className="flex justify-center mb-8">
            <button
              onClick={handleShare}
              className="flex items-center gap-2 px-4 py-2 bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-200 rounded-[24px] shadow-sm transition-colors duration-200"
              aria-label="Share"
            >
              <Share2 className="h-5 w-5" />
              Share
            </button>
          </div>
          {/* Image */}
          {post.image && (
            <div className="mb-10 flex justify-center">
              <img
                src={post.image}
                alt={post.title}
                className="rounded-[24px] max-h-[400px] w-auto object-contain shadow"
              />
            </div>
          )}
          {/* Content */}
          <div
            className="prose prose-lg max-w-none dark:prose-invert prose-green mx-auto"
            dangerouslySetInnerHTML={{ __html: post.content }}
          />
        </div>
      </section>
    </div>
  );
};

export default BlogPostPage; 